import { z } from "zod";
import { isoDate, isoDateTime, optionalText, uuid, wallTime } from "./common";

export const APPOINTMENT_STATUSES = [
  "requested",
  "booked",
  "confirmed",
  "checked_in",
  "in_consultation",
  "completed",
  "cancelled",
  "no_show",
  "rescheduled",
] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

/** Statuses that occupy the doctor/room/chair (the database exclusion constraints use the same list). */
export const HOLDING_STATUSES: readonly AppointmentStatus[] = ["booked", "confirmed", "checked_in", "in_consultation"];

export const APPOINTMENT_SOURCES = ["reception", "online", "whatsapp", "phone", "walk_in", "ai"] as const;

export const APPOINTMENT_ACTIONS = ["book", "confirm", "check_in", "start", "complete", "no_show", "cancel"] as const;
export type AppointmentAction = (typeof APPOINTMENT_ACTIONS)[number];

/** Allowed transitions. The API enforces this table; the UI uses it to show only valid buttons. */
export const TRANSITIONS: Record<AppointmentAction, { from: readonly AppointmentStatus[]; to: AppointmentStatus }> = {
  book: { from: ["requested"], to: "booked" },
  confirm: { from: ["requested", "booked"], to: "confirmed" },
  check_in: { from: ["booked", "confirmed"], to: "checked_in" },
  start: { from: ["checked_in"], to: "in_consultation" },
  complete: { from: ["checked_in", "in_consultation"], to: "completed" },
  no_show: { from: ["requested", "booked", "confirmed"], to: "no_show" },
  cancel: { from: ["requested", "booked", "confirmed", "checked_in"], to: "cancelled" },
};

export const allowedActions = (status: AppointmentStatus): AppointmentAction[] =>
  (Object.keys(TRANSITIONS) as AppointmentAction[]).filter((a) => TRANSITIONS[a].from.includes(status));

export const createAppointmentInput = z.object({
  patientId: uuid,
  doctorId: uuid,
  branchId: uuid,
  serviceId: uuid.nullable().default(null),
  startsAt: isoDateTime,
  /** Defaults to doctor/service duration. */
  durationMinutes: z.number().int().min(5).max(480).optional(),
  chairId: uuid.nullable().default(null),
  roomId: uuid.nullable().default(null),
  status: z.enum(["booked", "requested"]).default("booked"),
  source: z.enum(APPOINTMENT_SOURCES).default("reception"),
  notes: optionalText(1000),
  /** Book outside the doctor's working hours (emergencies). Needs appointments.edit and is audited. */
  allowOutsideSchedule: z.boolean().default(false),
  /** Set when booking from a waitlist entry; marks it booked in the same transaction. */
  waitlistEntryId: uuid.optional(),
});
export type CreateAppointmentInput = z.infer<typeof createAppointmentInput>;

export const rescheduleInput = z.object({
  version: z.number().int().min(1),
  startsAt: isoDateTime,
  durationMinutes: z.number().int().min(5).max(480).optional(),
  doctorId: uuid.optional(),
  chairId: uuid.nullable().optional(),
  roomId: uuid.nullable().optional(),
  allowOutsideSchedule: z.boolean().default(false),
  reason: optionalText(500),
});

export const transitionInput = z.object({
  action: z.enum(APPOINTMENT_ACTIONS),
  version: z.number().int().min(1),
  reason: optionalText(500),
  priority: z.enum(["normal", "priority", "emergency"]).default("normal"),
});

export const appointmentListQuery = z.object({
  from: isoDateTime,
  to: isoDateTime,
  branchId: uuid.optional(),
  doctorId: uuid.optional(),
  patientId: uuid.optional(),
});

export const patientAppointmentsQuery = z.object({ limit: z.coerce.number().int().min(1).max(100).default(20) });

export const slotsQuery = z.object({
  doctorId: uuid,
  branchId: uuid,
  serviceId: uuid.optional(),
  from: isoDate,
  to: isoDate,
  durationMinutes: z.coerce.number().int().min(5).max(480).optional(),
  /** Ignore this appointment's own time (when rescheduling it). */
  excludeAppointmentId: uuid.optional(),
});

export interface Slot {
  start: string;
  end: string;
  chairId: string | null;
}

export interface Appointment {
  id: string;
  branchId: string;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  doctor: { id: string; fullName: string; calendarColor: string | null };
  service: { id: string; name: string; calendarColor: string | null } | null;
  roomId: string | null;
  chairId: string | null;
  chairName: string | null;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  source: (typeof APPOINTMENT_SOURCES)[number];
  notes: string | null;
  cancellationReason: string | null;
  queue: { id: string; tokenNumber: number; status: QueueStatus } | null;
  visitId: string | null;
  version: number;
  createdAt: string;
}

export interface AppointmentDetail extends Appointment {
  history: { fromStatus: string | null; toStatus: string; reason: string | null; changedAt: string; changedBy: string | null }[];
  notesLog: { id: string; body: string; author: string | null; createdAt: string }[];
}

export const appointmentNoteInput = z.object({ body: z.string().trim().min(1).max(2000) });

/* --------------------------------------------------------------------------------------- queue */

export const QUEUE_STATUSES = ["waiting", "called", "with_doctor", "completed", "left"] as const;
export type QueueStatus = (typeof QUEUE_STATUSES)[number];

export const queueQuery = z.object({ branchId: uuid, date: isoDate.optional() });

export const walkInInput = z.object({
  patientId: uuid,
  branchId: uuid,
  doctorId: uuid.nullable().default(null),
  priority: z.enum(["normal", "priority", "emergency"]).default("normal"),
  notes: optionalText(500),
});

export const queueActionInput = z.object({
  action: z.enum(["call", "start", "complete", "left", "assign"]),
  version: z.number().int().min(1),
  doctorId: uuid.optional(),
});

export interface QueueEntry {
  id: string;
  tokenNumber: number;
  status: QueueStatus;
  priority: "normal" | "priority" | "emergency";
  patient: { id: string; fullName: string; patientCode: string };
  doctor: { id: string; fullName: string } | null;
  appointmentId: string | null;
  appointmentStartsAt: string | null;
  checkedInAt: string;
  calledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  notes: string | null;
  version: number;
  /** The clinical visit opened when the consultation started (null until then). */
  visitId: string | null;
}

/* ------------------------------------------------------------------------------------ waitlist */

export const waitlistInput = z
  .object({
    patientId: uuid,
    preferredBranchId: uuid.nullable().default(null),
    preferredDoctorId: uuid.nullable().default(null),
    preferredServiceId: uuid.nullable().default(null),
    preferredDateFrom: isoDate.nullable().default(null),
    preferredDateTo: isoDate.nullable().default(null),
    preferredTimeFrom: wallTime.nullable().default(null),
    preferredTimeTo: wallTime.nullable().default(null),
    priority: z.number().int().min(0).max(100).default(0),
    notes: optionalText(1000),
  })
  .refine((w) => !w.preferredDateFrom || !w.preferredDateTo || w.preferredDateTo >= w.preferredDateFrom, {
    message: "End date is before start date",
    path: ["preferredDateTo"],
  })
  .refine((w) => !w.preferredTimeFrom || !w.preferredTimeTo || w.preferredTimeTo > w.preferredTimeFrom, {
    message: "End time must be after start time",
    path: ["preferredTimeTo"],
  });

export const waitlistQuery = z.object({ status: z.enum(["open", "offered", "booked", "expired", "cancelled", "active"]).default("active") });

export interface WaitlistEntry {
  id: string;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  preferredBranchId: string | null;
  preferredDoctor: { id: string; fullName: string } | null;
  preferredService: { id: string; name: string } | null;
  preferredDateFrom: string | null;
  preferredDateTo: string | null;
  preferredTimeFrom: string | null;
  preferredTimeTo: string | null;
  priority: number;
  status: "open" | "offered" | "booked" | "expired" | "cancelled";
  notes: string | null;
  createdAt: string;
  version: number;
}

/** Returned when a cancellation frees a slot: waitlisted patients who fit it, best first. */
export interface FreedSlot {
  doctorId: string;
  branchId: string;
  startsAt: string;
  endsAt: string;
  matches: WaitlistEntry[];
}
